import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRef, useState } from "react";
import {
  AccountBalanceWallet,
  AccountCircle,
  BarChart,
  DonutSmall,
  Home,
  Info,
  Inventory,
  LocalShipping,
  Logout,
  ReceiptLong,
  Settings,
  ShoppingCart,
} from "@mui/icons-material";
import useLogout from "../../shared/hooks/useLogout";
import { useNavigate } from "react-router";
import { PAGES_URL, SIDEBAR_ID } from "../../shared/constants/constants";
import { getUserContext } from "../../shared/utils/utils";
import Footer from "../../components/footer/footer";
import { ReactComponent as PrismLogoNavCollapsed } from "../../shared/assets/prismai/prismai-navlogo.svg";
import { ReactComponent as PrismLogoNavExpanded } from "../../shared/assets/prismai/prismai-logo.svg";
import { Stack } from "@mui/material";
import { InternalAccessGate } from "../privateRoute/permissionsGate";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isPhone?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isPhone",
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ isPhone }) => !isPhone,
      style: {
        zIndex: theme.zIndex.drawer + 1,
      },
    },
    {
      props: ({ open, isPhone }) => Boolean(open && !isPhone),
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

interface CustomDrawerProps {
  open?: boolean;
  isPhone?: boolean;
}

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "isPhone",
})<CustomDrawerProps>(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open, isPhone }) => Boolean(open && !isPhone),
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open, isPhone }) => Boolean(!open && !isPhone),
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
    {
      props: ({ isPhone }) => Boolean(isPhone),
      style: {
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
        },
      },
    },
  ],
}));

interface DrawerProps {
  children?: React.ReactNode;
}

export default function MiniDrawer({ children }: DrawerProps) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const { logout } = useLogout();
  const navigate = useNavigate();
  const userContext: any = getUserContext();
  const orgPartyName = userContext.orgPartyName;

  const [open, setOpen] = useState(false);
  const pendingNavRef = useRef<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string>("");

  const iconStyle = {
    color: theme.palette.primary.main,
  };

  const ListElements = [
    {
      id: SIDEBAR_ID.HOME,
      url: PAGES_URL.DASHBOARD,
      icon: <Home style={iconStyle} />,
      text: "Home",
      onClick: () => menuClick(SIDEBAR_ID.HOME, PAGES_URL.DASHBOARD),
    },
    {
      id: SIDEBAR_ID.RFQ_LIST,
      url: `${PAGES_URL.ORDERMGR}/${PAGES_URL.RFQ_LIST}`,
      icon: <BarChart style={iconStyle} />,
      text: "Requests",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.RFQ_LIST,
          `${PAGES_URL.ORDERMGR}/${PAGES_URL.RFQ_LIST}`,
        ),
    },
    {
      id: SIDEBAR_ID.ORDER_LIST,
      url: `${PAGES_URL.ORDERMGR}/${PAGES_URL.ORDER_LIST}`,
      icon: <ShoppingCart style={iconStyle} />,
      text: "Purchase Orders",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.ORDER_LIST,
          `${PAGES_URL.ORDERMGR}/${PAGES_URL.ORDER_LIST}`,
        ),
    },
    {
      id: SIDEBAR_ID.GRN_LIST,
      url: `${PAGES_URL.ORDERMGR}/${PAGES_URL.GRN_LIST}`,
      icon: <LocalShipping style={{ ...iconStyle }} />,
      text: "Delivery Challans",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.GRN_LIST,
          `${PAGES_URL.ORDERMGR}/${PAGES_URL.GRN_LIST}`,
        ),
    },
    {
      id: SIDEBAR_ID.INVOICE,
      url: `/${PAGES_URL.ACCOUNT_MGR}/${PAGES_URL.INVOICE}`,
      icon: <ReceiptLong style={iconStyle} />,
      text: "Invoices",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.INVOICE,
          `/${PAGES_URL.ACCOUNT_MGR}/${PAGES_URL.INVOICE}`,
        ),
    },
    {
      id: SIDEBAR_ID.INVENTORY,
      url: `${PAGES_URL.ORDERMGR}/${PAGES_URL.INVENTORY}`,
      icon: <Inventory style={iconStyle} />,
      text: "Inventory",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.INVENTORY,
          `${PAGES_URL.ORDERMGR}/${PAGES_URL.INVENTORY}`,
        ),
    },
    {
      id: SIDEBAR_ID.REPORTING,
      url: `${PAGES_URL.DASHBOARD}/${PAGES_URL.REPORTING}`,
      icon: <DonutSmall style={iconStyle} />,
      text: "Reports",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.REPORTING,
          `${PAGES_URL.DASHBOARD}/${PAGES_URL.REPORTING}`,
        ),
    },
    {
      id: SIDEBAR_ID.SETTINGS,
      url: `${PAGES_URL.DASHBOARD}/${PAGES_URL.SETTINGS}`,
      icon: <Settings style={iconStyle} />,
      text: "Administration",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.SETTINGS,
          `${PAGES_URL.DASHBOARD}/${PAGES_URL.SETTINGS}`,
        ),
    },
    {
      id: SIDEBAR_ID.HELP,
      url: `${PAGES_URL.DASHBOARD}/${PAGES_URL.HELP}`,
      icon: <Info style={iconStyle} />,
      text: "Help",
      onClick: () =>
        menuClick(SIDEBAR_ID.HELP, `${PAGES_URL.DASHBOARD}/${PAGES_URL.HELP}`),
    },
    {
      id: SIDEBAR_ID.SELLERS,
      url: `${PAGES_URL.DASHBOARD}/${PAGES_URL.SELLER_ONBOARDING}`,
      icon: <AccountBalanceWallet style={iconStyle} />,
      text: "Sellers",
      onClick: () =>
        menuClick(SIDEBAR_ID.SELLERS, `${PAGES_URL.DASHBOARD}/${PAGES_URL.SELLER_ONBOARDING}`),
      internalAccess: true,
    },
    {
      id: SIDEBAR_ID.PROFILE,
      url: `${PAGES_URL.DASHBOARD}/${PAGES_URL.PROFILE}`,
      icon: <AccountCircle style={iconStyle} />,
      text: "Profile",
      onClick: () =>
        menuClick(
          SIDEBAR_ID.PROFILE,
          `${PAGES_URL.DASHBOARD}/${PAGES_URL.PROFILE}`,
        ),
    },
    {
      id: SIDEBAR_ID.LOGOUT,
      url: ``,
      icon: <Logout style={iconStyle} />,
      text: "Logout",
      onClick: () => logout(),
    },
  ];

  const menuClick = (dashboardMenuSelected: string, navLink: string) => {
    setSelectedItem(dashboardMenuSelected);
    if (!isPhone) {
      navigate(navLink);
      return;
    }
    pendingNavRef.current = navLink;
    handleDrawerClose();
  };

  const handleDrawerExited = () => {
    const next = pendingNavRef.current;
    if (next) {
      pendingNavRef.current = null;
      navigate(next);
    }
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} isPhone={isPhone}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[
              {
                marginRight: 5,
              },
              open && !isPhone && { display: "none" },
            ]}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {orgPartyName}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isPhone ? "temporary" : "permanent"}
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        SlideProps={{ onExited: handleDrawerExited }}
        isPhone={isPhone}
      >
        <DrawerHeader>
          {open && <PrismLogoNavExpanded />}
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {ListElements.map((element) => {
            const listItemContent = (
              <ListItem key={element.id} disablePadding sx={{ display: "block" }}>
                <Tooltip
                  title={element.text}
                  placement="right"
                  arrow
                  disableHoverListener={open || isPhone}
                >
                  <ListItemButton
                    onClick={element.onClick}
                    sx={[
                      {
                        minHeight: 48,
                        px: 2.5,
                      },
                      open || isPhone
                        ? {
                            justifyContent: "initial",
                          }
                        : {
                            justifyContent: "center",
                          },
                    ]}
                  >
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: "center",
                        },
                        open || isPhone
                          ? {
                              mr: 3,
                            }
                          : {
                              mr: "auto",
                            },
                      ]}
                    >
                      {element.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={element.text}
                      sx={[
                        open || isPhone
                          ? {
                              opacity: 1,
                            }
                          : {
                              opacity: 0,
                            },
                      ]}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );

            if (element.internalAccess) {
              return (
                <InternalAccessGate key={element.id}>
                  {listItemContent}
                </InternalAccessGate>
              );
            }

            return listItemContent;
          })}
          {!open && (
            <ListItem disablePadding sx={{ display: "block" }}>
              <Stack
                sx={{
                  minHeight: 48,
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PrismLogoNavCollapsed />
              </Stack>
            </ListItem>
          )}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1.5, md: 3 }, width: "100%" }}>
        <DrawerHeader />
        {children}
        <Footer />
      </Box>
    </Box>
  );
}